import React, { useState } from 'react';
import { Mail, ArrowLeft, Send } from 'lucide-react';
import { apiCall } from '../../config/api';
import { sendPasswordResetEmail } from '../../utils/emailService';
import AuthLayout from './AuthLayout';

const ForgotPassword = ({ onBack }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [formData, setFormData] = useState({
    email: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setMessage({ type: '', text: '' });
  };

  // Check email and send reset link
  const handleSendResetLink = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.email.includes('@')) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Step 1: Check if email exists using POST
      const checkUrl = '/users/check-email';
      console.log('🔍 Checking email with endpoint:', checkUrl);
      console.log('📧 Email:', formData.email);
      console.log('📦 Payload:', JSON.stringify({ email: formData.email }));

      const checkResponse = await apiCall(checkUrl, {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify({
          email: formData.email
        })
      });

      console.log('Response status:', checkResponse.status);
      const checkData = await checkResponse.json();
      console.log('Check email response:', checkData);

      if (!checkData.success) {
        setMessage({ type: 'error', text: checkData.message || 'Email not found in our system' });
        setLoading(false);
        return;
      }

      // Step 2: Email exists (success: true), send password reset email
      console.log('✅ Email verified, sending reset link...');
      const emailResult = await sendPasswordResetEmail({
        email: formData.email,
        name: checkData.name || checkData.username || 'User'
      });

      if (emailResult.success) {
        setMessage({
          type: 'success',
          text: 'Password reset link has been sent to your email. Please check your inbox.'
        });
        console.log('Reset link:', emailResult.resetLink);
      } else {
        setMessage({
          type: 'error',
          text: `Email verified but failed to send reset link: ${emailResult.error}`
        });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An error occurred. Please try again.' });
      console.error('Forgot password error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="w-full max-w-[500px] bg-white rounded-3xl shadow-xl border border-slate-100 p-6 sm:p-8 animate-scaleIn">
        {/* Logo */}
        <div className="text-center mb-6 animate-slideDown">
          <div className="inline-flex items-center justify-center mb-2">
            <img
              src="/images/logo.png"
              alt="QuickLook Logo"
              className="h-20 sm:h-24 w-auto"
            />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1">
            Reset Your Password
          </h2>
          <p className="text-slate-400 text-xs sm:text-sm">
            Enter your email address and we'll send you a reset link
          </p>
        </div>

        {/* Back Button */}
        <button
          onClick={onBack}
          className="flex items-center text-slate-500 hover:text-slate-700 mb-6 text-sm"
        >
          <ArrowLeft size={18} className="mr-2" />
          Back to Login
        </button>

        {/* Message Display */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-xl text-sm ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-600'
          }`}>
            {message.text}
          </div>
        )}

        {/* Email Input Form */}
        <form onSubmit={handleSendResetLink} className="space-y-4 sm:space-y-5 relative">
          {/* Loading Bar */}
          {loading && (
            <div className="absolute -top-4 left-0 right-0 h-1 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 animate-[loadingBar_2s_linear]" style={{animation: 'loadingBar 2s linear', animationFillMode: 'forwards'}} />
            </div>
          )}
          <style>{`
            @keyframes loadingBar {
              from { width: 0%; }
              to { width: 100%; }
            }
          `}</style>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-slate-600 mb-1.5 sm:mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email address"
                className="w-full pl-11 pr-4 py-2.5 sm:py-3 text-sm sm:text-base border border-slate-200 rounded-2xl bg-slate-50 text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none focus:bg-white"
                required
                disabled={loading}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-bold text-base py-3 px-4 rounded-2xl transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-1 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending Reset Link...
              </>
            ) : (
              <>
                Send Reset Link
                <Send size={16} />
              </>
            )}
          </button>
        </form>
      </div>
    </AuthLayout>
  );
};

export default ForgotPassword;
